import React, { useState, useEffect } from 'react';
import { ShieldCheck, UserPlus, Mail, Phone, Lock, Tag, Users, AlertCircle, Loader2, CheckCircle, ExternalLink, Edit2, Search, Trash2 } from 'lucide-react';
import { formatWhatsAppNumber } from '../../utils/phoneUtils';
import { createAdminUser, getAllUsers, toggleUserStatus, updateUserPermissions, updateAdminPassword } from '../../services/userService';
import { updateSystemSettings } from '../../services/configService';
import { useAuth } from '../../context/AuthContext';

export default function UserManagement() {
  const { systemSettings, isSuperAdmin, userProfile } = useAuth();
  const allBranches = systemSettings?.systemBranches || [];
  
  const systemBranches = isSuperAdmin 
    ? allBranches 
    : allBranches.filter(b => userProfile?.assignedBranches?.includes(b.id) || userProfile?.assignedBranches?.includes(b.parentId));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create Form States
  const [isCreating, setIsCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  
  // Password Edit States
  const [editingPasswordId, setEditingPasswordId] = useState(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [formData, setFormData] = useState({
    displayName: '',
    username: '',
    phone: '',
    password: '',
    assignedBranches: []
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      let data = await getAllUsers();
      if (!isSuperAdmin) {
         data = data.filter(u => u.parentAdminId === userProfile.uid || u.id === userProfile.uid);
      }
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleToggleStatus = async (uid, currentStatus) => {
    if(!window.confirm(`Kullanıcı erişimini ${currentStatus ? 'askıya almak' : 'tekrar aktif hale getirmek'} istediğinize emin misiniz?`)) return;
    try {
      await toggleUserStatus(uid, currentStatus);
      loadUsers();
    } catch (err) {
      console.error(err);
      alert('İşlem başarısız oldu.');
    }
  };

  const handleTogglePermission = async (uid, currentPermissions, field) => {
    const updatedPermissions = {
       ...currentPermissions,
       [field]: !currentPermissions?.[field]
    };
    try {
       await updateUserPermissions(uid, updatedPermissions);
       loadUsers();
    } catch (err) {
       console.error(err);
       alert("Yetki güncellenemedi.");
    }
  };

  const handleBranchToggle = (branchId) => {
    setFormData(prev => {
      const branches = [...prev.assignedBranches];
      if (branches.includes(branchId)) {
        return { ...prev, assignedBranches: branches.filter(b => b !== branchId) };
      } else {
        return { ...prev, assignedBranches: [...branches, branchId] };
      }
    });
  };

  const submitPasswordUpdate = async (user) => {
     if (newPasswordInput.length < 6) {
        alert("Şifre en az 6 karakter olmalıdır.");
        return;
     }
     setIsUpdatingPassword(true);
     try {
        await updateAdminPassword(user.email, user.rawPassword, newPasswordInput, user.id);
        setEditingPasswordId(null);
        setNewPasswordInput('');
        loadUsers();
     } catch (err) {
        console.error(err);
        alert("Şifre güncellenirken bir hata oluştu: " + err.message);
     } finally {
        setIsUpdatingPassword(false);
     }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setIsCreating(true);

    try {
      if (formData.password.length < 6) {
        throw new Error('Şifre en az 6 karakter olmalıdır.');
      }
      
      const creationData = {
         ...formData,
         role: isSuperAdmin ? 'admin' : 'subAdmin',
         isSuperAdminAction: isSuperAdmin,
         parentAdminId: isSuperAdmin ? null : userProfile.uid
      };
      
      await createAdminUser(creationData);
      
      // WhatsApp Yönlendirmesi
      const text = `Hasımoğlu Çiftliği yönetim sistemine erişiminiz tanımlandı.\n\nKullanıcı Adı: ${formData.username}\nŞifre: ${formData.password}\n\nYönetim Paneli Giriş:\nhttps://hasimoglucifligi.com/login`;
      
      const waLink = `https://wa.me/${formatWhatsAppNumber(formData.phone)}?text=${encodeURIComponent(text)}`;
      
      setFormSuccess('Kullanıcı başarıyla oluşturuldu. WhatsApp bağlantısı açılıyor...');
      setTimeout(() => {
        window.open(waLink, '_blank');
      }, 500);

      // Formu temizle ve listeyi güncelle
      setFormData({ displayName: '', username: '', phone: '', password: '', assignedBranches: [] });
      loadUsers();
      
    } catch (err) {
      setFormError(err.message || 'Kullanıcı oluşturulurken bir hata oluştu. Kullanıcı Adı (ID) zaten kullanımda olabilir.');
    } finally {
      setIsCreating(false);
    }
  };

  
  const filteredUsers = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = (u.displayName || '').toLowerCase().includes(q);
    const userMatch = (u.username || '').toLowerCase().includes(q);
    const phoneMatch = (u.phone || '').includes(q);
    return nameMatch || userMatch || phoneMatch;
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-gold-400" />
          Kullanıcı ve Rol Yönetimi
        </h2>
        <p className="text-sm text-navy-400 mt-1">Sisteme yeni alt yöneticiler ekleyin ve şube yetkilerini belirleyin.</p>
      </div>

        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-8">

        
        {/* Sol Taraf: Yeni Kullanıcı Ekleme Formu */}
        <div className="lg:col-span-1">
          <div className="glass rounded-2xl border border-navy-700/50 overflow-hidden h-full flex flex-col">
            <div className="px-5 py-4 border-b border-navy-700/50 bg-navy-900/50">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-forest-400" />
                {isSuperAdmin ? 'Yeni Yönetici (Admin) Ekle' : 'Yeni Alt Yönetici Ekle'}
              </h3>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-5 space-y-4">
              {formError && (
                <div className="flex items-start gap-2 bg-danger-500/10 border border-danger-500/20 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-danger-500">{formError}</p>
                </div>
              )}
              {formSuccess && (
                <div className="flex items-start gap-2 bg-forest-500/10 border border-forest-500/20 rounded-lg p-3">
                  <CheckCircle className="w-4 h-4 text-forest-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-forest-400 font-medium">{formSuccess}</p>
                    <p className="text-[10px] text-forest-500 mt-0.5">Engellendiyse popup izinlerini kontrol edin.</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1">Ad Soyad</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-navy-400"><Users className="w-4 h-4" /></span>
                  <input
                    type="text" required
                    value={formData.displayName} onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1">Kullanıcı Adı (ID)</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-navy-400"><Mail className="w-4 h-4" /></span>
                  <input
                    type="text" required placeholder="örn: ahmet.kurtkoy"
                    value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '')})}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1">Telefon (WhatsApp için)</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-navy-400"><Phone className="w-4 h-4" /></span>
                  <input
                    type="tel" required placeholder="05XX..."
                    value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1">Geçici Şifre</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-navy-400"><Lock className="w-4 h-4" /></span>
                  <input
                    type="text" required minLength="6"
                    value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-navy-700/50">
                <label className="block text-xs font-semibold text-navy-200 mb-2">Yetkili Olacağı Şubeler</label>
                <div className="space-y-3">
                  {systemBranches.filter(b => !b.parentId).map(parent => (
                    <div key={parent.id} className="bg-navy-900/30 rounded-lg p-2 border border-navy-700/50">
                       <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-navy-800/50 transition-colors">
                         <input
                           type="checkbox"
                           checked={formData.assignedBranches.includes(parent.id)}
                           onChange={() => handleBranchToggle(parent.id)}
                           className="w-5 h-5 rounded border-navy-600 text-forest-600 focus:ring-forest-500 bg-navy-800"
                         />
                         <span className="text-sm font-bold text-white">{parent.name} (Merkez)</span>
                       </label>
                       
                       {/* Alt Şubeler - Sadece Merkez seçiliyse açılsın */}
                       {formData.assignedBranches.includes(parent.id) && systemBranches.filter(sub => sub.parentId === parent.id).length > 0 && (
                          <div className="ml-8 mt-2 space-y-1 border-l-2 border-navy-700 pl-3 animate-in fade-in slide-in-from-top-2">
                             <p className="text-xs text-gold-400 mb-2">Merkezin altındaki noktalar eklensin mi? (Opsiyonel)</p>
                             {systemBranches.filter(sub => sub.parentId === parent.id).map(sub => (
                               <label key={sub.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-navy-800/50 transition-colors">
                                 <input
                                   type="checkbox"
                                   checked={formData.assignedBranches.includes(sub.id)}
                                   onChange={() => handleBranchToggle(sub.id)}
                                   className="w-4 h-4 rounded border-navy-600 text-cyan-500 focus:ring-cyan-500 bg-navy-800"
                                 />
                                 <span className="text-sm font-medium text-navy-200">↳ {sub.name} (Alt Şube)</span>
                               </label>
                             ))}
                          </div>
                       )}
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isCreating}
                className="w-full mt-4 py-3 rounded-xl font-bold text-white bg-forest-600 hover:bg-forest-500 transition-colors flex items-center justify-center gap-2 shadow-lg"
              >
                {isCreating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                {isCreating ? 'İşleniyor...' : 'Hesap Aç ve SMS(WA) Gönder'}
              </button>
            </form>
            </div>
          </div>

          {!isSuperAdmin && (
            <div className="lg:col-span-1 h-full flex flex-col">
             <div className="glass rounded-2xl border border-navy-700/50 overflow-hidden h-full flex flex-col">
               <div className="px-5 py-4 border-b border-navy-700/50 bg-navy-900/50">
                 <h3 className="text-base font-semibold text-white flex items-center gap-2">
                   <Tag className="w-4 h-4 text-gold-400" />
                   Yeni Alt Şube Tanımla
                 </h3>
               </div>
               <div className="p-5 space-y-4">
                 <div>
                   <label className="block text-xs font-medium text-navy-200 mb-1">Alt Şube ID (Boşluksuz)</label>
                   <input
                     id="subBranchId"
                     type="text" placeholder="örn: gebze_merkez"
                     className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-gold-500 outline-none text-sm"
                   />
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-navy-200 mb-1">Alt Şube Adı (Görünen)</label>
                   <input
                     id="subBranchName"
                     type="text" placeholder="örn: Gebze Merkez Dağıtım"
                     className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-gold-500 outline-none text-sm"
                   />
                 </div>
                 <button
                   onClick={async () => {
                     const sid = document.getElementById('subBranchId').value.trim().toLowerCase();
                     const sname = document.getElementById('subBranchName').value.trim();
                     if(!sid || !sname) return alert("Bilgileri doldurun");
                     if(allBranches.find(b => b.id === sid)) return alert("Bu ID kullanımda");
                     
                     // Sadece kendi ana şubelerinden ilkini parent olarak alıyor veya dropdown eklenebilir. Şimdilik ilk atanmış şubesi:
                     const parentId = userProfile.assignedBranches[0];
                     if(!parentId) return alert("Önce size bir ana şube atanmalı.");

                     const newBranch = { id: sid, name: sname, parentId, color: 'from-navy-600 to-navy-800' };
                     await updateSystemSettings({ systemBranches: [...allBranches, newBranch] });
                     alert("Alt şube başarıyla eklendi! Sayfa yenilendiğinde listede görebilirsiniz.");
                     window.location.reload();
                   }}
                   className="w-full py-2.5 rounded-xl font-bold text-navy-950 bg-gold-500 hover:bg-gold-400 transition-colors flex items-center justify-center gap-2"
                 >
                   <ExternalLink className="w-4 h-4" />
                   Alt Şubeyi Kaydet
                 </button>
               </div>
               
               {/* Mevcut Alt Şubeler Listesi & Silme */}
               <div className="p-5 border-t border-navy-700/50 bg-navy-900/30 max-h-48 overflow-y-auto custom-scrollbar flex-1">
                 <h4 className="text-xs font-semibold text-navy-400 mb-3 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Mevcut Alt Şubeleriniz</h4>
                 {allBranches.filter(b => b.parentId === userProfile?.assignedBranches?.[0]).length === 0 ? (
                    <p className="text-xs text-navy-500 italic">Henüz tanımlı alt şubeniz yok.</p>
                 ) : (
                    <div className="space-y-2">
                       {allBranches.filter(b => b.parentId === userProfile?.assignedBranches?.[0]).map(sub => (
                          <div key={sub.id} className="flex items-center justify-between bg-navy-800/50 px-3 py-2 rounded border border-navy-700/50 hover:border-navy-600 transition-colors">
                             <div>
                                <span className="block text-sm font-medium text-navy-200">{sub.name}</span>
                                <span className="block text-[9px] text-navy-500 font-mono">ID: {sub.id}</span>
                             </div>
                             <button
                                onClick={async () => {
                                   if(!window.confirm(`"${sub.name}" alt şubesini sistemden KALICI olarak silmek istediğinize emin misiniz?\n\n(ÖNEMLİ: Bu şubeye atanmış kurbanlar veya personeller varsa askıda kalabilir. Önce onların aktarımını yapmış olduğunuzdan emin olun!)`)) return;
                                   const newBranches = allBranches.filter(b => b.id !== sub.id);
                                   await updateSystemSettings({ systemBranches: newBranches });
                                   alert("Alt şube başarıyla silindi!");
                                   window.location.reload();
                                }}
                                className="text-danger-400 hover:bg-danger-500/20 p-1.5 rounded transition-colors border border-transparent hover:border-danger-500/30"
                                title="Alt Şubeyi Sistemden Sil"
                             >
                                <Trash2 className="w-4 h-4" />
                             </button>
                          </div>
                       ))}
                    </div>
                 )}
               </div>
             </div>
            </div>
          )}
        </div>

        </div>

        {/* Sağ Taraf: Yönetici Listesi */}
        <div className="w-full space-y-4">
          <div className="flex flex-col xl:flex-row items-center gap-2">
            <div className="flex items-center gap-1 bg-navy-900/60 p-1.5 rounded-xl border border-navy-700/50 backdrop-blur-md w-full xl:w-auto overflow-x-auto custom-scrollbar">
               <button onClick={() => setRoleFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${roleFilter === 'all' ? 'bg-navy-600 text-white shadow-lg' : 'text-navy-400 hover:text-white hover:bg-navy-800'}`}>Tümü</button>
               <button onClick={() => setRoleFilter('superAdmin')} className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1 ${roleFilter === 'superAdmin' ? 'bg-navy-800/80 text-gold-400 border border-gold-500/50 shadow-lg' : 'text-navy-400 hover:text-white hover:bg-navy-800'}`}>Süper Admin</button>
               <button onClick={() => setRoleFilter('admin')} className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1 ${roleFilter === 'admin' ? 'bg-navy-800/80 text-forest-400 border border-forest-500/50 shadow-lg' : 'text-navy-400 hover:text-white hover:bg-navy-800'}`}>Ana Şube (Admin)</button>
               <button onClick={() => setRoleFilter('subAdmin')} className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1 ${roleFilter === 'subAdmin' ? 'bg-navy-800/80 text-cyan-400 border border-cyan-500/50 shadow-lg' : 'text-navy-400 hover:text-white hover:bg-navy-800'}`}>Alt Şube (Personel)</button>
            </div>
            
            <div className="glass rounded-xl p-2.5 border border-navy-700/50 flex flex-1 items-center gap-3 w-full">
               <Search className="w-5 h-5 text-navy-400 shrink-0 ml-1" />
               <input
                 type="text"
                 placeholder="İsim, Kullanıcı Adı veya Telefon..."
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
                 className="w-full bg-transparent border-none text-white focus:ring-0 outline-none placeholder:text-navy-500 text-sm py-1"
               />
            </div>
          </div>

          {loading ? (

            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-forest-500 animate-spin" /></div>
          ) : filteredUsers.length === 0 ? (
             <div className="glass rounded-2xl p-8 text-center text-navy-400">Sistemde henüz hesap bulunmuyor.</div>
          ) : (
            filteredUsers.map(u => (
              <React.Fragment key={u.id}>
                <div className={`glass p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-navy-700/50 hover:border-navy-600 transition-colors ${u.role !== 'superAdmin' ? 'rounded-t-xl' : 'rounded-xl'}`}>
                  <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg shrink-0 ${u.role === 'superAdmin' ? 'bg-gradient-to-br from-gold-500 to-gold-700' : 'bg-gradient-to-br from-navy-600 to-navy-800'}`}>
                    {u.role === 'superAdmin' ? <ShieldCheck className="w-6 h-6 text-white" /> : <ShieldCheck className="w-6 h-6 text-forest-400" />}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white leading-tight flex items-center gap-2">
                      {u.displayName || u.username || (u.email ? u.email.split('@')[0] : 'Admin')}
                      {u.isActive === false && <span className="text-[10px] bg-danger-500 px-1.5 py-0.5 rounded text-white font-bold ml-1">ASKIYA ALINDI</span>}
                    </h4>
                    <div className="flex items-center gap-3 mt-1 text-xs text-navy-400">
                      <span className="flex items-center gap-1 font-mono text-gold-400/80">ID: {u.username || (u.email ? u.email.split('@')[0] : 'Yok')}</span>
                      {u.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {u.phone}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-start md:items-end w-full md:w-auto mt-4 md:mt-0 gap-2">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                     {/* GİZLİ ŞİFRE PANELİ SÜPER ADMİN İÇİN */}
                     {u.rawPassword && (
                        <div className="flex items-center gap-1 bg-navy-950/60 border border-navy-700/50 px-2 py-1 rounded">
                           <Lock className="w-3 h-3 text-gold-500" />
                           {editingPasswordId === u.id ? (
                              <div className="flex items-center gap-1">
                                 <input
                                    type="text"
                                    value={newPasswordInput}
                                    onChange={(e) => setNewPasswordInput(e.target.value)}
                                    placeholder="Yeni Şifre"
                                    className="bg-navy-900 text-xs px-2 py-0.5 rounded border border-navy-600 outline-none text-white w-24 focus:border-gold-500"
                                    disabled={isUpdatingPassword}
                                    autoFocus
                                 />
                                 <button onClick={() => submitPasswordUpdate(u)} disabled={isUpdatingPassword} className="text-forest-400 p-0.5 hover:bg-forest-500/20 rounded">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                 </button>
                                 <button onClick={() => setEditingPasswordId(null)} disabled={isUpdatingPassword} className="text-navy-400 p-0.5 hover:bg-danger-500/20 rounded">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                 </button>
                              </div>
                           ) : (
                              <>
                                 <span className="text-xs font-mono text-gold-400 selectable select-all">Şifre: {u.rawPassword}</span>
                                 <button onClick={() => { setEditingPasswordId(u.id); setNewPasswordInput(u.rawPassword); }} className="ml-2 text-navy-400 hover:text-white p-0.5 rounded transition-colors bg-navy-800/80">
                                   <Edit2 className="w-3 h-3" />
                                 </button>
                              </>
                           )}
                        </div>
                     )}

                     <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${
                       u.role === 'superAdmin' ? 'bg-gold-500/10 text-gold-400 border-gold-500/20' : 
                       u.role === 'admin' ? 'bg-forest-900/40 text-forest-400 border-forest-500/30' : 
                       'bg-cyan-900/40 text-cyan-400 border-cyan-500/30'
                     }`}>
                       {u.role === 'superAdmin' ? 'Süper Admin' : u.role === 'admin' ? 'Ana Şube Yöneticisi' : 'Alt Şube (Personel)'}
                     </span>
                  </div>

                  
                  <div className="flex flex-col md:flex-row w-full items-center justify-between md:justify-end gap-3 mt-2">
                     {/* ŞUBE BİLGİLERİ */}
                     {u.role !== 'superAdmin' && u.assignedBranches && (
                       <div className="flex flex-wrap gap-1 justify-end">
                         {u.assignedBranches.length === 0 ? (
                           <span className="text-[10px] text-danger-400 flex items-center gap-1"><Tag className="w-3 h-3"/> Şube Atanmadı</span>
                         ) : (
                           u.assignedBranches.map(b => (
                             <span key={b} className="text-[10px] px-1.5 py-0.5 rounded bg-navy-800 text-white border border-navy-700">
                               {b.toUpperCase()}
                             </span>
                           ))
                         )}
                       </div>
                     )}

                     {/* İŞLEMLER */}
                     {u.role !== 'superAdmin' && u.id !== userProfile?.uid && (
                        <button 
                           onClick={() => handleToggleStatus(u.id, u.isActive !== false ? true : false)}
                           className={`text-xs px-3 py-1.5 rounded font-bold transition-all ${
                              u.isActive === false 
                              ? 'bg-forest-600/20 text-forest-500 hover:bg-forest-600/40 border border-forest-500/30' 
                              : 'bg-danger-500/10 text-danger-500 hover:bg-danger-500/20 border border-danger-500/20'
                           }`}
                        >
                           {u.isActive === false ? 'Erişimi Aç' : 'Hesabı Dondur'}
                        </button>
                     )}
                  </div>
                </div>
              </div>
              {/* DİNAMİK YETKİ MATRİSİ (SüperAdminler VEYA Kendi Alt/Sub-Adminlerini Yöneten Adminler Görebilir) */}
              {u.role !== 'superAdmin' && u.id !== userProfile?.uid && (
                 <div className="bg-navy-900/30 border-t border-navy-700/50 p-3 flex flex-wrap gap-4 rounded-b-xl -mt-2 text-[11px] w-full items-center justify-start md:pl-20">
                    <span className="text-navy-400 font-bold uppercase tracking-wider">İleri Düzey Yetkiler:</span>
                    
                    <label className="flex items-center gap-2 cursor-pointer group">
                       <input 
                         type="checkbox" 
                         className="hidden" 
                         checked={u.permissions?.canDeletePayment || false}
                         onChange={() => handleTogglePermission(u.id, u.permissions, 'canDeletePayment')}
                       />
                       <div className={`w-8 h-4 rounded-full transition-colors relative ${u.permissions?.canDeletePayment ? 'bg-forest-500' : 'bg-navy-700'}`}>
                          <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${u.permissions?.canDeletePayment ? 'left-4.5 translate-x-4' : 'left-0.5'}`}></div>
                       </div>
                       <span className={`font-bold transition-colors ${u.permissions?.canDeletePayment ? 'text-forest-400' : 'text-navy-500'}`}>Tahsilat/Fatura Silebilir</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer group">
                       <input 
                         type="checkbox" 
                         className="hidden" 
                         checked={u.permissions?.canDeleteShareholder || false}
                         onChange={() => handleTogglePermission(u.id, u.permissions, 'canDeleteShareholder')}
                       />
                       <div className={`w-8 h-4 rounded-full transition-colors relative ${u.permissions?.canDeleteShareholder ? 'bg-forest-500' : 'bg-navy-700'}`}>
                          <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${u.permissions?.canDeleteShareholder ? 'left-4.5 translate-x-4' : 'left-0.5'}`}></div>
                       </div>
                       <span className={`font-bold transition-colors ${u.permissions?.canDeleteShareholder ? 'text-forest-400' : 'text-navy-500'}`}>Hissedar Silebilir</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer group">
                       <input 
                         type="checkbox" 
                         className="hidden" 
                         checked={u.permissions?.canEditAnimal || false}
                         onChange={() => handleTogglePermission(u.id, u.permissions, 'canEditAnimal')}
                       />
                       <div className={`w-8 h-4 rounded-full transition-colors relative ${u.permissions?.canEditAnimal ? 'bg-forest-500' : 'bg-navy-700'}`}>
                          <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${u.permissions?.canEditAnimal ? 'left-4.5 translate-x-4' : 'left-0.5'}`}></div>
                       </div>
                       <span className={`font-bold transition-colors ${u.permissions?.canEditAnimal ? 'text-forest-400' : 'text-navy-500'}`}>Kurbanı Tamamen Silebilir</span>
                    </label>
                 </div>
              )}
             </React.Fragment>
            ))
          )}
        </div>
    </div>
  );
}
